<!--
  Route-fork PR-R4: Client Portal token auto-login landing.

  The Client Portal's token landing (/portal/invite/:token). The legacy
  `/admin-buyer-dashboard/:id` landing and its re-export shim were deleted
  with the Admin Buyer dashboard (card #566). The backend endpoint
  that redeems the token is `/auth/loginClientPortalWithToken/:token`
  (renamed from `loginAdminBuyerWithToken`, card #566).

  Flow:
    1. Mount with the token in the route params.
    2. POST/GET /auth/loginClientPortalWithToken/:token. The response shape
       (issue #901, PR-12) matches /auth/login: {token, account_id,
       capabilities, distributor, campaign_id, ...}.
    3. Clear any stale session via store.dispatch("logout") so we land
       cleanly even if a different user was previously authenticated in
       this browser.
    4. Persist the new session token + userId in localStorage; verify the
       write took (#907 guard).
    5. Fetch the SAFRS-shaped account so legacy Vuex consumers
       (state.account.role.name, etc.) hydrate alongside the Pinia
       capabilities (the {account, authResponse} dual-shape).
    6. Await the login dispatch so reactivity flushes before navigation
       (#907 fix).
    7. Route to /portal?store=<campaign_id> (plain /portal when the token
       names no store). The scope store honors the requested store only when
       it is one of the caller's allowed stores.

  Phase 1 lands the Overview / Orders / Approvals / Budget / Team /
  Settings stubs that /portal can resolve. Phase 4 fills them in.
-->
<template>
    <v-container fluid class="fill-height py-0 mr-0 pl-1 pr-2">
        <div
            v-if="errorMsg"
            class="d-flex align-center justify-center"
            data-testid="portal-auto-login-error"
        >
            <h1>{{ errorMsg }}</h1>
        </div>
    </v-container>
</template>

<script>
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const REFUSAL = "This link is invalid or has expired.";

export default {
    name: "PortalAutoLogin",

    data() {
        return {
            errorMsg: null,
        };
    },

    computed: {
        loading() {
            return this.$store.getters.loading;
        },
    },

    async mounted() {
        // The one route that mounts this page is /portal/invite/:token.
        const token = this.$route.params.token;
        if (!token) return;

        // The param is decoded by vue-router (%2F arrives as "/"), so it must
        // never be spliced into an API path unchecked: `..%2F..%2Fauth%2Fme`
        // would send a same-origin GET to another endpoint. Issued tokens are
        // uuid4 hex; accept the URL-safe alphabet and refuse the rest with the
        // same uniform refusal, before any request.
        if (!TOKEN_PATTERN.test(String(token))) {
            this.errorMsg = REFUSAL;
            return;
        }

        let campaignId = null;
        // Errors are caught and shown as the refusal; nothing here logs, so a
        // failed request cannot put the token URL in the console.
        try {
            const resp = await this.$axios.get(
                `/auth/loginClientPortalWithToken/${encodeURIComponent(token)}`
            );
            if (resp.data.message) {
                this.errorMsg = resp.data.message;
                return;
            }

            // Clear any prior session in localStorage + Pinia + Vuex.
            await this.$store.dispatch("logout");

            // Persist the new session. Strip any quote wrappers that the
            // backend serializer may include (defensive).
            const cleanToken = String(resp.data.token).replace(
                /^["']+|["']+$/g,
                ""
            );
            localStorage.setItem("token", cleanToken);
            localStorage.setItem("userId", resp.data.account_id);

            // #907 guard: if localStorage didn't actually persist (private
            // browsing, quota, etc.) the subsequent requests will fail
            // mysteriously. Surface that as the refusal.
            if (localStorage.getItem("token") !== cleanToken) {
                this.errorMsg = REFUSAL;
                return;
            }

            const account = await this.$store.dispatch("jv/get", [
                `accounts/${resp.data.account_id}`,
                { params: { include: "role" } },
            ]);

            // PR-12 (issue #901): dual-shape so Pinia + Vuex both hydrate.
            // #907: await the dispatch so the route guard sees populated
            // capabilities before navigation. GH #2941 defect 2: the token
            // identifies a PERSON, so clear any anonymous-storefront marker.
            await this.$store.dispatch("login", {
                account,
                authResponse: resp.data,
                credentialed: true,
            });
            campaignId = resp.data.campaign_id;
        } catch (_e) {
            this.errorMsg = REFUSAL;
            return;
        }

        // replace, not push: the token URL must not stay in history.
        // The token names the store its email was about (the close reminder
        // says "extend your close date"), so request that store: PortalLayout
        // passes ?store= to the scope store, which selects it ONLY if it is
        // one of this caller's stores. Only an all-digit id is forwarded.
        const storeId = String(campaignId ?? "");
        if (/^\d+$/.test(storeId)) {
            this.$router.replace({ path: "/portal", query: { store: storeId } });
        } else {
            this.$router.replace("/portal");
        }
    },
};
</script>

<style scoped>
.background {
    position: absolute;
    width: 100%;
    height: 100%;
}
</style>
